import React from 'react';
import { kpiCardLabel, kpiCardValue, kpiDisplayValue, kpiTiedLabel } from './kpiFormat';

// One value, one optional label (PRD-1578). The card shows no title over a KPI: the
// label under the value is what names it, so "Shifts … 1,000 shifts" no longer says
// the same word twice. `displayLabel` is the author's edit; without one the generated
// unit + subtitle stand in (kpiFormat.js::kpiCardLabel). The header already says when
// it was fetched and for which range, so the tile shows nothing else.
//
// `labelMode` is the enable_widget_text_edit flag. Off, the tile is drawn as before:
// the value carries its unit ("1,000 shifts") and the generated subtitle sits under it.
//
// Same prop contract as the chart renderers ({ data, width, height, xLabel, yLabel })
// so the card and the wizard preview can treat every type alike; the axis labels
// have no meaning here and are ignored. `data` is the whole single-value payload
// (see renderers/index.js::rendererInput), not the category-value `data` array.

// The figure is real text, sized to fill the cell it lives in. Height sets the
// ceiling (the value plus the small lines under it have to fit under the header);
// width sets the other, from the number of characters — tabular digits run about
// 0.6em each, so a 7-character "55,753" needs ~4.2em of width. The smaller wins,
// clamped so a tiny cell is still legible and a huge one does not become a poster.
//
// `smallLines` counts the lines under the value: the subtitle and the tie. Each is
// on its own line, so neither takes width from the value and neither can be clipped
// by it; they only cost height.
const MIN_PX = 24;
const MAX_PX = 160;
const CHAR_EM = 0.62;
const HEIGHT_SHARE = [0.8, 0.62, 0.5];
export function valueFontPx(width, height, text, smallLines = 0) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (!w || !h) return MIN_PX;
  const chars = Math.max(1, String(text || '').length);
  // Value line ≈1.05em, each small line ≈0.26em, plus the gaps: ~1.45em with one
  // small line, ~1.75em with two.
  const lines = Math.min(HEIGHT_SHARE.length - 1, Math.max(0, Number(smallLines) || 0));
  const byHeight = h * HEIGHT_SHARE[lines];
  const byWidth = (w * 0.9) / (chars * CHAR_EM);
  return Math.round(Math.max(MIN_PX, Math.min(MAX_PX, byHeight, byWidth)));
}

function KpiRenderer({ data, width, height, title, displayLabel, labelMode = false }) {
  const shown = labelMode ? kpiCardValue(data) : kpiDisplayValue(data);
  const subtitle = labelMode ? kpiCardLabel(data, displayLabel) : String(data?.value_label || '').trim();
  const tied = kpiTiedLabel(data);
  const label = [title, shown, tied, subtitle].filter(Boolean).join(', ');
  const px = valueFontPx(width, height, shown, [tied, subtitle].filter(Boolean).length);
  // The subtitle follows the value at a fixed ratio so the pair reads as one unit.
  const subPx = Math.round(Math.max(12, Math.min(22, px * 0.2)));
  // The tie sits on its own line under the value, outside the value's ellipsis, and
  // rides along on the hover that recovers an ellipsized value.
  return (
    <div className="wc-kpi" role="figure" aria-label={label}>
      <div className="wc-kpi-value" style={{ fontSize: `${px}px` }} title={tied ? `${shown}, ${tied}` : shown}>
        {shown}
      </div>
      {tied && <div className="wc-kpi-tied" style={{ fontSize: `${subPx}px` }}>{tied}</div>}
      {subtitle && <div className="wc-kpi-subtitle" style={{ fontSize: `${subPx}px` }}>{subtitle}</div>}
    </div>
  );
}

export default KpiRenderer;
